import React, {useState, useEffect, useRef} from 'react';
import {registerBrick} from 'olympe';
import {ReactBrick, useProperty} from '@olympeio/core';
import {
    Autocomplete,
    TextField,
    Chip,
    Box,
    Checkbox,
} from '@mui/material';
import {useDebounce, useLocalStorage} from './_helpers';
import {getCompanyCodesLambda} from '../utils/functionSignatures';

/**
 * Component to filter company codes with chip inputs
 */
export default class CompanyCodeFilter extends ReactBrick {
    // eslint-disable-next-line require-jsdoc
    static getReactComponent($) {
        return (props) => {
            const companyCodes = useProperty($, 'companyCodes') || [];
            const [companyData, setCompanyData] = useState([]);
            const [companyCodesLS, setCompanyCodeLS] = useLocalStorage('companyCodes', []);
            const [inputValue, setInputValue] = useState('');
            const inputRef = useRef(null);
            const debouncedTextInput = useDebounce(inputValue, 400);
            const [companyCodeLoading, setCompanyCodeLoading] = useState(false);
            // Check if there are company codes saved in local storage and sync them with component state
            useEffect(() => {
                const storedFilters = JSON.parse(localStorage.getItem('companyCodes')) ?? [];
                setCompanyCodeLS(storedFilters);
            }, []);

            useEffect(() => {
                if (companyCodesLS) {
                    $.set('companyCodes', companyCodesLS);
                }
            }, [companyCodesLS]);

            useEffect(()=>{
                if ( !companyCodeLoading ) {
                    setCompanyCodeLoading(true);
                    const companyCodesPromise = getCompanyCodesLambda($, $.get('getCompanyCodes'), debouncedTextInput);

                    companyCodesPromise.then((res) => {
                        if (res) {
                            const retrievedData = JSON.parse(res).map((item) => item.bukrs);
                            setCompanyData([...new Set(retrievedData)] || []);
                        }
                        setCompanyCodeLoading(false);
                    }).finally(()=>{
                        setCompanyCodeLoading(false);
                    });
                }
            }, [debouncedTextInput]);

            // This useEffect handles pasting multiple company codes separated in 4 character chunks
            useEffect(() => {
                if (inputRef.current) {
                    const handlePaste = (event) => {
                        event.preventDefault();
                        let paste = (event.clipboardData || window.clipboardData).getData('text');
                        paste = paste.replace(/\s+/g, '');
                        const newChips = [];
                        for (let i = 0; i < paste.length; i += 4) {
                            const chip = paste.substring(i, i + 4).trim();
                            if (chip) {
                                newChips.push(chip);
                            }
                        }
                        addChips(newChips);
                        setInputValue('');
                        inputRef.current.value = '';
                    };

                    const inputElement = inputRef.current;
                    inputElement.addEventListener('paste', handlePaste);

                    return () => {
                        inputElement.removeEventListener('paste', handlePaste);
                    };
                }
            }, [inputRef.current]);

            const handleAddChip = (newChip) => {
                if (newChip.trim() && !companyCodes.includes(newChip.trim())) {
                    const newCompanyCodes = [...companyCodes, newChip.trim()];
                    setCompanyCodeLS(newCompanyCodes);
                }
            };

            const addChips = (chips) => {
                const newCompanyCodes = [...companyCodes];
                chips.forEach((chip) => {
                    if (chip.trim() && !newCompanyCodes.includes(chip.trim())) {
                        newCompanyCodes.push(chip.trim());
                    }
                });
                setCompanyCodeLS(newCompanyCodes);
                setInputValue('');
            };

            const handleDeleteChip = (chipToDelete) => {
                const newCompanyCodes = companyCodes.filter((code) => code !== chipToDelete);
                setCompanyCodeLS(newCompanyCodes);
            };

            const handleKeyDown = (event) => {
                if (event.key === 'Backspace' && event.target.value === '') {
                    handleDeleteChip(companyCodes[companyCodes.length - 1]);
                }
            };

            const handleToggleChip = (option) => {
                if (companyCodes.includes(option)) {
                    handleDeleteChip(option);
                } else {
                    handleAddChip(option);
                    setInputValue('');
                }
            };

            const handleBlur = (event) => {
                const value = event.target.value.trim().toUpperCase();
                if (value) {
                    handleAddChip(value);
                    setInputValue('');
                    if (inputRef.current) {
                        inputRef.current.value = '';
                    }
                }
            };

            return (
                <Autocomplete
                    multiple
                    freeSolo
                    limitTags={3}
                    disableClearable
                    options={companyData}
                    getOptionLabel={(option) => (companyData.length > 0 ? option : null)}
                    value={companyCodes}
                    sx={{
                        'minWidth': '5rem',
                        '& .MuiInputBase-input': {
                            minWidth: '4rem !important',
                        },
                        '& .MuiInputBase-root': {
                            'border': 'none',
                            '&:before': {
                                borderBottom: 'none',
                            },
                            '&:after': {
                                borderBottom: 'none',
                            },
                            '&:hover:not(.Mui-disabled):before': {
                                borderBottom: 'none',
                            },
                        },
                    }}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => {
                        // fetch the new things
                        setInputValue(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                        const lastValue = newValue[newValue.length - 1];
                        if (typeof lastValue === 'string') {
                            handleAddChip(lastValue.toUpperCase());
                            setInputValue('');
                            if (inputRef.current) {
                                inputRef.current.value = '';
                            }
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                key={index}
                                label={option}
                                {...getTagProps({index})}
                                onDelete={() => handleDeleteChip(option)}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'nowrap', minWidth: '5rem', width: 'auto', marginTop: '10px'}}>
                            <TextField
                                {...params}
                                inputRef={inputRef}
                                variant="outlined"
                                label="Company Code"
                                size={'small'}
                                onKeyDown={handleKeyDown}
                                onBlur={handleBlur}
                                sx={{flex: '1 1 auto'}}
                            />
                        </Box>
                    )}
                    renderOption={(props, option) => (
                        <li
                            {...props}
                            key={option}
                            onClick={(event) => {
                                event.stopPropagation();
                                handleToggleChip(option);
                            }}
                        >
                            <Checkbox
                                checked={companyCodes.includes(option)}
                                sx={{marginRight: 1, color: '#2196f3 !important'}}
                            />
                            {option}
                        </li>
                    )}
                />
            );
        };
    }
}

registerBrick('018ff1d0eec8559eae4e', CompanyCodeFilter);
